import { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Popconfirm,
  MessagePlugin,
  Switch,
  type PrimaryTableCol,
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon, SearchIcon } from 'tdesign-icons-react';
import { businessCardLevelApi } from '../../api';

const { FormItem } = Form;
const { Option } = Select;

const levelTypeMap: Record<number, { label: string; theme: 'primary' | 'success' }> = {
  1: { label: '企业', theme: 'primary' },
  2: { label: '个人', theme: 'success' },
};

export default function EcardLevel() {
  const [levels, setLevels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentLevel, setCurrentLevel] = useState<any | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [searchForm] = Form.useForm();
  const [formRef] = Form.useForm();
  const formInstanceRef = useRef<any>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchLevels = async (params: { pageNum?: number; pageSize?: number } = {}) => {
    setLoading(true);
    try {
      const rawSearchParams = searchForm.getFieldsValue?.(true) || {};
      const searchParams = Object.keys(rawSearchParams).reduce((acc, key) => {
        const value = rawSearchParams[key];
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const res = await businessCardLevelApi.getPageList({
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams,
        ...params,
      });
      if (res.code === 200) {
        const list = res.data?.list || res.data?.records || [];
        setLevels(list);
        setPagination({
          ...pagination,
          total: res.data?.total || 0,
          ...(params.pageNum && { current: params.pageNum }),
          ...(params.pageSize && { pageSize: params.pageSize }),
        });
      }
    } catch (error) {
      MessagePlugin.error('获取套餐列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const resetFormState = () => {
    setCurrentLevel(null);
    if (formInstanceRef.current) {
      formInstanceRef.current.reset();
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchLevels({ pageNum: 1 });
  };

  const handleReset = () => {
    searchForm.reset();
    setPagination({ ...pagination, current: 1 });
    fetchLevels({ pageNum: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ ...pagination, current: page, pageSize });
    fetchLevels({ pageNum: page, pageSize });
  };

  const handleAdd = () => {
    resetFormState();
    setVisible(true);
  };

  const handleEdit = async (record: any) => {
    try {
      const res = await businessCardLevelApi.getById(record.id);
      if (res.code === 200) {
        setCurrentLevel(res.data);
        setVisible(true);
      } else {
        MessagePlugin.error('获取套餐详情失败');
      }
    } catch {
      MessagePlugin.error('获取套餐详情失败');
    }
  };

  const handleFormSubmit = async () => {
    try {
      const validateResult = formInstanceRef.current?.validate();
      if (validateResult !== true && Object.keys(validateResult).length > 0) {
        MessagePlugin.warning('请完善表单信息');
        return;
      }
      const values = formInstanceRef.current?.getFieldsValue(true);
      const submitData: any = {
        ...values,
        levelType: Number(values.levelType),
        levelOrigPrice: Number(values.levelOrigPrice),
        levelSalesPrice: Number(values.levelSalesPrice),
        levelUserNum: Number(values.levelUserNum),
        sortOrder: Number(values.sortOrder) || 0,
        status: values.status ? 1 : 0,
      };

      const res = currentLevel
        ? await businessCardLevelApi.update(currentLevel.id, submitData)
        : await businessCardLevelApi.create(submitData);
      if (res.code === 200) {
        MessagePlugin.success(currentLevel ? '更新成功' : '创建成功');
        setVisible(false);
        setCurrentLevel(null);
        if (formInstanceRef.current) {
          formInstanceRef.current.reset();
        }
        fetchLevels();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (error) {
      MessagePlugin.error('提交失败，请稍后重试');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await businessCardLevelApi.delete(id);
      MessagePlugin.success('删除成功');
      setSelectedRowKeys([]);
      fetchLevels();
    } catch (error) {
      MessagePlugin.error('删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      MessagePlugin.warning('请先选择要删除的数据');
      return;
    }
    try {
      await businessCardLevelApi.deleteBatch(selectedRowKeys as number[]);
      MessagePlugin.success('批量删除成功');
      setSelectedRowKeys([]);
      fetchLevels();
    } catch (error) {
      MessagePlugin.error('批量删除失败');
    }
  };

  const toggleSelectRow = (rowKey: number) => {
    setSelectedRowKeys((prev) => {
      if (prev.includes(rowKey)) {
        return prev.filter((id) => id !== rowKey);
      }
      return [...prev, rowKey];
    });
  };

  const handleStatusToggle = async (row: any) => {
    try {
      const newStatus = row.status === 1 ? 0 : 1;
      await businessCardLevelApi.update(row.id, { ...row, status: newStatus });
      MessagePlugin.success(newStatus === 1 ? '已启用' : '已禁用');
      fetchLevels();
    } catch (error) {
      MessagePlugin.error('状态更新失败');
    }
  };

  const columns: PrimaryTableCol<any>[] = [
    {
      colKey: 'selection',
      title: () => (
        <input
          type="checkbox"
          checked={levels.length > 0 && levels.every((item) => selectedRowKeys.includes(item.id))}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys(levels.map((o) => o.id));
            } else {
              setSelectedRowKeys([]);
            }
          }}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
      width: 50,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={selectedRowKeys.includes(row.id)}
          onChange={() => toggleSelectRow(row.id)}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
    },
    {
      colKey: 'levelName',
      title: '套餐名称',
      width: 160,
      cell: ({ row }: { row: any }) => row.levelName || '-',
    },
    {
      colKey: 'levelType',
      title: '套餐类型',
      width: 110,
      cell: ({ row }: { row: any }) => {
        const info = levelTypeMap[row.levelType];
        return info ? <Tag theme={info.theme} variant="light">{info.label}</Tag> : '-';
      },
    },
    {
      colKey: 'levelDesc',
      title: '套餐描述',
      width: 200,
      ellipsis: true,
      cell: ({ row }: { row: any }) => row.levelDesc || '-',
    },
    {
      colKey: 'levelOrigPrice',
      title: '原价(元/年)',
      width: 120,
      cell: ({ row }: { row: any }) => (
        <span className="text-gray-500 line-through">¥{row.levelOrigPrice?.toFixed(2) || '0.00'}</span>
      ),
    },
    {
      colKey: 'levelSalesPrice',
      title: '售价(元/年)',
      width: 120,
      cell: ({ row }: { row: any }) => (
        <span className="font-medium text-[#1c5bff]">¥{row.levelSalesPrice?.toFixed(2) || '0.00'}</span>
      ),
    },
    {
      colKey: 'levelUserNum',
      title: '包含人数',
      width: 100,
      cell: ({ row }: { row: any }) => row.levelUserNum ?? '-',
    },
    {
      colKey: 'sortOrder',
      title: '排序',
      width: 80,
      cell: ({ row }: { row: any }) => row.sortOrder ?? 0,
    },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: { row: any }) => (
        <Tag theme={row.status === 1 ? 'success' : 'default'} variant="light">
          {row.status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      colKey: 'createTime',
      title: '创建时间',
      width: 170,
      cell: ({ row }: { row: any }) => row.createTime || '-',
    },
    {
      colKey: 'action',
      title: '操作',
      width: 220,
      fixed: 'right',
      cell: ({ row }: { row: any }) => (
        <Space>
          <Button theme="primary" variant="text" size="small" onClick={() => handleEdit(row)} icon={<EditIcon />}>
            编辑
          </Button>
          <Button
            theme={row.status === 1 ? 'warning' : 'success'}
            variant="text"
            size="small"
            onClick={() => handleStatusToggle(row)}
          >
            {row.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm content="确定删除该套餐吗？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" size="small" icon={<DeleteIcon />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold">套餐管理</h1>
        <Space>
          <Button theme="primary" onClick={handleAdd} icon={<AddIcon />}>
            新增套餐
          </Button>
          <Popconfirm content="确定批量删除选中的套餐吗？" onConfirm={handleBatchDelete}>
            <Button theme="danger" disabled={selectedRowKeys.length === 0} icon={<DeleteIcon />}>
              批量删除
            </Button>
          </Popconfirm>
          {selectedRowKeys.length > 0 && (
            <span className="text-sm text-gray-500">
              已选择 <span className="text-[#1c5bff] font-medium">{selectedRowKeys.length}</span> 项
            </span>
          )}
        </Space>
      </div>

      {/* 搜索区域 */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <Form form={searchForm} layout="inline">
          <FormItem name="levelName" label="套餐名称">
            <Input placeholder="请输入套餐名称" clearable style={{ width: 180 }} />
          </FormItem>
          <FormItem name="levelType" label="套餐类型">
            <Select placeholder="请选择套餐类型" clearable style={{ width: 150 }}>
              <Option value={1}>企业</Option>
              <Option value={2}>个人</Option>
            </Select>
          </FormItem>
          <FormItem name="status" label="状态">
            <Select placeholder="请选择状态" clearable style={{ width: 120 }}>
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </FormItem>
          <FormItem>
            <Button theme="primary" onClick={handleSearch} icon={<SearchIcon />}>
              查询
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>
              重置
            </Button>
          </FormItem>
        </Form>
      </div>

      {/* 表格区域 */}
      <div className="bg-white rounded-lg shadow-sm">
        <Table
          rowKey="id"
          data={levels}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showJumper: true,
            onChange: (pageInfo) => handlePageChange(pageInfo.current, pageInfo.pageSize),
          }}
          selectOnRowClick
          selectedRowKeys={selectedRowKeys}
          onSelectChange={(val: any) => setSelectedRowKeys(val)}
        />
      </div>

      {/* 新增/编辑 弹窗 */}
      <Dialog
        header={currentLevel ? '编辑套餐' : '新增套餐'}
        visible={visible}
        onClose={() => {
          setVisible(false);
          resetFormState();
        }}
        onConfirm={handleFormSubmit}
        confirmBtn="保存"
        width={680}
        key={currentLevel ? `edit-${currentLevel.id}` : 'add-new'}
      >
        <Form
          ref={formInstanceRef}
          labelWidth={110}
          initialData={currentLevel || undefined}
        >
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormItem label="套餐名称" name="levelName" rules={[{ required: true, message: '请输入套餐名称' }]}>
                <Input placeholder="请输入套餐名称" />
              </FormItem>
              <FormItem label="套餐类型" name="levelType" rules={[{ required: true, message: '请选择套餐类型' }]}>
                <Select placeholder="请选择套餐类型">
                  <Option value={1}>企业</Option>
                  <Option value={2}>个人</Option>
                </Select>
              </FormItem>
              <FormItem label="套餐描述" name="levelDesc" className="md:col-span-2">
                <Input placeholder="请输入套餐描述" />
              </FormItem>
              <FormItem label="原价(元/年)" name="levelOrigPrice" rules={[{ required: true, message: '请输入原价' }]}>
                <Input placeholder="请输入原价" type="number" />
              </FormItem>
              <FormItem label="售价(元/年)" name="levelSalesPrice" rules={[{ required: true, message: '请输入售价' }]}>
                <Input placeholder="请输入售价" type="number" />
              </FormItem>
              <FormItem label="包含人数" name="levelUserNum" rules={[{ required: true, message: '请输入包含人数' }]}>
                <Input placeholder="请输入包含人数" type="number" />
              </FormItem>
              <FormItem label="排序" name="sortOrder">
                <Input placeholder="请输入排序值（越小越靠前）" type="number" />
              </FormItem>
              {/* <FormItem label="状态" name="status" className="md:col-span-2">
                <div className="flex items-center gap-3">
                  <Switch
                    defaultChecked={currentLevel ? currentLevel.status === 1 : true}
                    onChange={(checked) => {
                      if (formInstanceRef.current) {
                        formInstanceRef.current.setFieldsValue({ status: checked ? 1 : 0 });
                      }
                    }}
                  />
                  <span className="text-sm text-gray-500">
                    {currentLevel ? (currentLevel.status === 1 ? '启用' : '禁用') : '启用'}
                  </span>
                </div>
              </FormItem> */}
            </div>
          </div>
        </Form>
      </Dialog>
    </div>
  );
}